<template>
    <div ref="productList" class="rounded-[10px] bg-[#2B2C32] w-full">
        <div class="text-[24px] text-white px-4 pt-4">
            <div class="flex justify-between">
                <span class="font-semibold">Inventory Overview</span>
            </div>
        </div>
        <div class="table w-full mt-6 px-2 pb-4">
            <!-- @do-search="searchProduct" -->
            <new-table-lite
                :is-slot-mode="true"
                :is-loading="table.loading"
                :do-clear-checked="table.clearChecked"
                @cleared-checked="clearedChecked"
                :class="'block w-full min-h-[480px] admin-purchase-order'"
                :rowClasses="row => row?'id-'+row.id:null"
                :columns="table.columns"
                :rows="table.rows"
                :total="table.totalRecords"
                :sortable="table.sortable"
                :page-size="table.pagesize"
                :page="table.page"
                @do-search="changePage"
                :pageOptions='table.pageOptions'
                :messages="{
                    pagingInfo: 'Total: {2}',
                    pageSizeChangeLabel: 'Items per Page',
                    gotoPageLabel: 'Go to page',
                    noDataAvailable: 'No records found!',
                }"
            >
                <!-- filter -->
                <template v-slot:tableCaption>
                    <div class="flex flex-row m-2 gap-2 z-20">
                        <div class="w-full">
                            <input placeholder="Search by name, SKU, GTIN" name="search" v-model="search" class="productName w-full rounded h-[42px] bg-theme-modalbg ring-0 outline-0 px-2 mb-1 text-white border border-transparent focus:border focus:border-theme-green">
                        </div>
                        <div class="w-full">
                            <select-category v-model="selectedCategory" class="w-full rounded bg-theme-modalbg mb-1 text-white border border-transparent focus:border focus:border-theme-green" placeholder="Categories" :options="categories"></select-category>
                        </div>
                        <div v-if="!resized" class="w-full">
                            <select-brand v-model="selectedBrand" classString="top-[42px]" class="w-full rounded bg-theme-modalbg mb-1 text-white border border-transparent focus:border focus:border-theme-green" placeholder="Brands" :options="brands"></select-brand>
                        </div>
                        <div v-if="!resized" class="w-full">
                            <SelectCheckboxMinOne :options="productStatus" v-model="selectedProductStatus" placeholder="Choose" class="w-full rounded bg-theme-modalbg mb-1 text-white border border-transparent focus:border focus:border-theme-green" />
                        </div>
                    </div>
                </template>
                <template v-slot:name="data">
                    <div @click="selectedRow(data.value)" class="flex flex-col cursor-pointer">
                        <div class="hover:opacity-70">{{ data.value.name }}</div>
                        <div class="text-grey">{{ data.value.barcode }}</div>
                    </div>
                </template>
                <template v-slot:ppiStock="data">
                    <div class="relative w-[100px]">
                        <span @mouseover="showUnitStock(data.value.id)" @mouseleave="removeShowUnitStock" class="underline cursor-pointer" >{{ getTotalStock(data.value) }} Units</span>
                        <div v-if="showUnitStockId==data.value.id" @mouseover="showUnitStock(data.value.id)" @mouseleave="showUnitStock(null)" class="absolute top-8 left-[-90px] w-60   mt-1  z-10 rounded-md shadow-lg">
                            <div class="absolute left-28 -top-4 h-0 w-0 border-x-8 border-x-transparent border-b-[16px] border-b-gray-black"></div>
                            <div class="bg-gray-black rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                <div class="py-2 px-6 border-b border-gray-400">{{ getTotalStock(data.value) }} units in {{ getTotalLocations(data.value) }} locations</div>
                                <ul role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-item-3" class="max-h-[360px] overflow-auto">
                                    <li id="listbox-item-0" role="option" class="text-gray-900 select-none relative py-2 px-3">
                                        <div class="grid grid-cols-10 gap-5">
                                            <span class="col-span-6 ml-3 block text-white font-normal">
                                                {{  data.value.locationName }}
                                            </span>
                                            <span class="col-span-4 block text-white font-normal">
                                                {{  data.value.ppiStock }} units
                                            </span>
                                        </div>
                                    </li>
                                    <template v-for="(product, ind) in data.value.productViewPricings" :key="ind">
                                        <li v-if="product.locations" role="option" class="text-gray-900 select-none relative py-2 px-3">
                                            
                                                <div class="grid grid-cols-10 gap-5">
                                                    <span class="col-span-6 ml-3 block text-white font-normal">
                                                        {{ product.locations?.name }}
                                                    </span>
                                                    <span class="col-span-4 block text-white font-normal">
                                                        {{ product.stock }} units
                                                    </span>
                                                </div>
                                        </li>
                                    </template>
                                </ul>
                            </div>
                        </div>
                    </div>
                </template>
                <template  v-slot:categoryId="data">
                   <div class="flex items-center w-[180px]">
                        <span class="inline-block w-5 h-5 mr-2 rounded" :style="`background-color:${getCategoryColor(data.value.categoryId)} `"></span>
                        <span class="inline-block truncate text-single-line align-top">{{ getCategoryName(data.value.categoryId) }}</span>
                    </div>
                </template>
                <template  v-slot:ppiPrice="data">
                   <span>{{ data.value.ppiPrice?'$'+data.value.ppiPrice:'' }}</span>
                </template>
            </new-table-lite>
        </div>
    </div>
    
</template>
<script>
import Env from '@/env';
import { defineComponent, reactive, ref} from "vue";
import {mapGetters, mapMutations} from 'vuex'
import { useSnackbar } from "vue3-snackbar"
import debounce from 'lodash/debounce'

// api
import BusinessProduct from '@/controllers/BusinessProductApis'
import InventoryApi from '@/controllers/InventoryApis'

// components
import NewTableLite from '@/components/table/tableLite.vue'
import SelectBrand from '@/components/form/SelectBrand.vue'
import SelectCategory from '@/components/form/SelectCategory.vue'
import SelectCheckboxMinOne from '@/components/form/SelectCheckboxMinOne.vue'


export default defineComponent({
    props:['resized'],
    components: {
        SelectBrand,
        SelectCategory,
        NewTableLite,
        SelectCheckboxMinOne
    },
    setup(){
        // list
        const snackbar = useSnackbar();
        const showUnitStockId = ref(false)
        const search = ref('')
        const selectedBrand = ref([])
        const selectedCategory = ref('')
        const checkedProduct = ref([])
        const table = reactive({
            loading: false,
            columns: [],
            rows: [],
            totalRecords: 0,
            page: 1,
            sortable: {
                order: "",
                sort: "",
            },
            pagesize: Env.defaultPageSize,
            pageOptions: Env.pageList,
            count: 0,
            clearChecked: false,
        })
        const categories = ref([])
        const brands = ref([])
        const selectedProductStatus = ref(['active'])
        const productStatus = ([
            {id: 'active',name: 'Active'},
            {id: 'out_of_stock',name: 'Out of stock'},
        ])
        const timer = null
        return {snackbar, showUnitStockId, search, selectedBrand, selectedCategory, checkedProduct, table, categories, brands, selectedProductStatus, productStatus, timer}
    },
    mounted(){
        if(this.$route.query.search){
            this.search = this.$route.query.search
        }
        this.selectTableColumn()
        this.fetchProduct()
        this.getCategory()
        this.getBrand()
    },
    beforeDestroy() {
    },
    watch: {
        search: debounce(async function(newVal, oldVal){
            if(newVal?.length != 2  &&  newVal?.length != 1) {
                this.searchProduct()
            }
        }, 900),
        'selectedBrand.length'(){
            this.searchProduct()
        },
        'selectedProductStatus.length'(){
            this.searchProduct()
        },
        selectedCategory(){
            this.searchProduct()
        },

        vg_selectedLocation(){
            if(this.vg_selectedBusiness?.id){
                this.selectTableColumn()
                this.fetchProduct()
                this.getCategory()
                this.getBrand()
                this.emitter.emit('closeAddEdit')
            }
        },
        resized(val){
            if(val){
                this.selectTableColumn('addEdit')
            }else{
                this.selectTableColumn()
            }
        }
    },
    computed: {
        ...mapGetters({
            vg_selectedBusiness: 'auth/activeBusiness',
            vg_selectedLocation: 'location/selected',
        })
    },
    methods: {
        getTotalLocations(obj){
            let total = 1
            for(let i=0; i<obj.productViewPricings.length; i++){
                if(obj.productViewPricings[i].locations){
                    total++
                }
            }
            return total
        },
        getTotalStock(obj){
            let total = obj.ppiStock
            for(let i=0; i<obj.productViewPricings.length; i++){
                if(obj.productViewPricings[i].locations){
                    total += obj.productViewPricings[i].stock
                }
            }
            return total
        },
        showUnitStock(id){
            clearTimeout(this.timer)
            this.showUnitStockId = id
        },
        removeShowUnitStock(){
            clearTimeout(this.timer)
            this.timer = setTimeout(() => {
                this.showUnitStockId = null
            }, 1000)
        },
        selectedRow(item){
            window.open(`/products?search=${item.barcode}&select=true`, '_blank')
        },
        selectTableColumn(type=''){
            if(type=='addEdit'){
                this.table.columns = [
                    {label: 'Id', field: 'id', sortable:false, isKey:true, columnClasses:'hidden', headerClasses:'hidden' },
                    {label: 'Name', field:'name', sortable: true},
                ]
            }else{
                this.table.columns = [
                    {label: 'Id', field: 'id', sortable:false, isKey:true, columnClasses:'hidden', headerClasses:'hidden' },
                    {label: 'Name', field:'name', sortable: true},
                    {label: 'SKU', field:'sku', sortable: false},
                    {label: 'Variant name', field:'variantName', sortable: false, width: '150px !important'},
                    {label: 'Units in Stock', field: 'ppiStock', sortable: false, width: '150px !important'},
                    {label: 'Price', field:'ppiPrice', sortable: false, width: '150px !important'},
                    {label: 'Category', field:'categoryId', sortable:false, width: '200px !important'}
                ]
            }
        },
        async getCategory(){
            this.categories = []
            let res = await BusinessProduct.fetchAllCategory()
            if(res.data?.payload?.category){
                //let rootCategory = res.data.payload.category.sort((a, b) => a.name.localeCompare(b.name))
                let rootCategory = res.data.payload.category
                for(let i=0; i<rootCategory.length; i++){
                    this.categories.push(rootCategory[i])
                    if(rootCategory[i].subCategories && rootCategory[i].subCategories.length>0){
                        for(let j=0; j<rootCategory[i].subCategories.length; j++){
                            this.categories.push(rootCategory[i].subCategories[j])
                        }
                    }
                }
            }
        },
        async getBrand(){
            let {data: {payload}} = await BusinessProduct.fetchAllBrands(this.vg_selectedLocation?.id)
            this.brands = payload.brands
        },
        getBrandName(brandId){
            if(this.brands?.length>0){
                let result = this.brands.find(each => each.id===brandId)
                if(result){
                    return result.name
                }
            }
            return ''
        },
        getCategoryName(categoryId){
            if(this.categories?.length>0){
                let result = this.categories.find(each => each.id===categoryId)
                if(result){
                    return result.name
                }
            }
            return ''
        },
        getCategoryName(categoryId){
            if(this.categories?.length>0){
                let result = this.categories.find(each => each.id===categoryId)
                if(result){
                    return result.name
                }
            }
            return ''
        },
        getCategoryColor(categoryId){
            if(this.categories?.length>0){
                let result = this.categories.find(each => each.id===categoryId)
                if(result){
                    return result.posColour
                }
            }
            return ''
        },
        changePage(offset, limit, column, sort){
            this.table.page = (offset/limit)+1
            this.fetchProduct(this.table.page, limit, column, sort)
        },
        async searchProduct(){
            this.fetchProduct()
        },
        async fetchProduct(page=1, limit=this.table.pagesize, column='', sort=''){
            let data = {
                searchTerm: this.search,
                brandIds: this.selectedBrand,
                productStatus: this.selectedProductStatus.length==2?null:this.selectedProductStatus[0]
            }
            if(this.selectedCategory){
                data.categoryId = this.selectedCategory=='noCategory'?null:this.selectedCategory
            }
            if(column && sort){
                data.sortField = column
                data.sortDirection = sort
            }
            this.table.loading = true
            this.table.clearChecked = false
            let params = `page=${page}&perPage=${limit}`
            let {data: {payload}} = await InventoryApi.fetchAllProduct(params, data)
            this.table.rows = payload.productList
            this.table.totalRecords = payload.totalRecords
            this.table.sortable.order = ''
            this.table.sortable.sort = ''
            this.table.clearChecked = true
            this.table.loading = false
        },
        ...mapMutations({
            vm_spinner: 'spinner/mutateSpinner',
            vm_modified: 'modifiedForm/mutateStatus'
        }),
    }
})
</script>
